/* Brainy Bounce · cart bridge for the hero toy basket.
   Adds a variant with Shopify's Ajax Cart API and announces it with the theme's standard
   CartLinesUpdateEvent, the same way the product form does, so the header count and the cart
   drawer re-render from Shopify's response. Success is only reported after Shopify confirms it. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

/**
 * @param {number|string} variantId - Shopify variant ID (from the section's product data)
 * @param {number} quantity
 * @param {EventTarget} [source] - element the event is dispatched from
 * @returns {Promise<object|null>} the Ajax cart after the add (null if it couldn't be re-read)
 */
async function add(variantId, quantity, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  // The basket shows its own "Added to cart" and opens the drawer when clicked, so a toy drop
  // shouldn't pop the drawer over the scene. Everything else about the event is unchanged.
  const drawer = document.querySelector('cart-drawer-component[auto-open]');
  if (drawer) drawer.removeAttribute('auto-open');
  try {
    (source || document).dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    }));
  } finally {
    if (drawer) drawer.setAttribute('auto-open', '');
  }

  let response, json;
  try {
    response = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: cartSectionIds().join(',') }),
    });
    json = await response.json();
  } catch (e) {
    const err = new Error('network');
    err.kind = 'network';
    deferred.reject(err);
    throw err;
  }
  if (!response.ok || json.status) {
    const err = new Error(json.description || json.message || 'add failed');
    err.kind = 'shopify';
    deferred.reject(err);
    throw err;
  }

  let cart = null;
  try {
    cart = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  } catch (e) { /* the add worked; the count catches up on the next cart update */ }
  deferred.resolve({
    cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
    detail: {
      items: cart ? cart.items : json.items,
      source: 'bb-toy-basket',
      itemCount: quantity,
      productId: json.items && json.items[0] && json.items[0].product_id,
      sections: json.sections,
      didError: false,
    },
  });
  return cart;
}

// Keep the basket count in step with every cart change (drawer edits, product page adds, ...)
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart }) => {
      if (cart && typeof cart.totalQuantity === 'number') {
        document.dispatchEvent(new CustomEvent('bb:cart-count', { detail: { count: cart.totalQuantity } }));
      }
    })
    .catch(() => {});
});

window.BBShop = { add };
document.dispatchEvent(new Event('bb:shop-ready'));
